import { GridBlockType } from 'services/entities/BlockTypes';

export type PageOrientation = 'portrait' | 'landscape';

export type PageSize = {
  label: string;
  width: number;
  height: number;
};

export const PAGE_SIZE_PORTRAIT_A4: PageSize = { label: 'a4', width: 794, height: 1123 };
export const PAGE_SIZE_PORTRAIT_LEGAL: PageSize = { label: 'legal', width: 816, height: 1344 };
export const PAGE_SIZE_US_PORTRAIT_LETTER: PageSize = { label: 'us_letter', width: 816, height: 1056 };

export const PAGE_SIZES: readonly PageSize[] = [PAGE_SIZE_PORTRAIT_A4, PAGE_SIZE_PORTRAIT_LEGAL, PAGE_SIZE_US_PORTRAIT_LETTER];
export const DEFAULT_PAGE_SIZE = PAGE_SIZE_US_PORTRAIT_LETTER;

export const gridPixelSize = 1;
export const gridPageMaxWidthInPixels = 816;
export const gridPageMinHeightInPixels = 1056;
export const gridDefaultPixelDensity = 96;
export const gridDefaultWidth = 712;
export const gridDefaultWidthInInches = 8.5;
export const gridDefaultHeight = 24;
export const gridDefaultHeightInInches = 11;
export const gridDefaultXAxis = 50;
export const gridDefaultYAxis = 66;
export const gridDefaultGreaterZAxis = 0;
export const gridSnapDistanceInPx = 2;
export const gridDragLinePreviewDistanceInPx = 8;

export const gridDefaultDimensionsOnDrop = {
  [GridBlockType.TEXT]: {
    width: 250,
    height: 250,
  },
  [GridBlockType.TABLE]: {
    width: 666,
    height: 158,
  },
  [GridBlockType.VIDEO]: {
    width: 500,
    height: 281, // 16:9 aspect ratio
  },
  [GridBlockType.SHAPE]: {
    width: 200,
    height: 200,
  },
  [GridBlockType.IMAGE]: {
    width: 320,
    height: 320,
  },
  [GridBlockType.EMBED]: {
    width: 744,
    height: 392,
  },
};
export const gridDefaultDimensionsOnTablePricing = {
  width: 721,
  height: 470,
};

// Accepts undefined so callers can pass a stored pageOrientation directly
// without a null-check; undefined is treated as portrait (the safe default).
export function getCanvasWidth(orientation: PageOrientation | undefined): number {
  return orientation === 'landscape' ? gridPageMinHeightInPixels : gridPageMaxWidthInPixels;
}

export function getCanvasMinHeight(orientation: PageOrientation | undefined): number {
  return orientation === 'landscape' ? gridPageMaxWidthInPixels : gridPageMinHeightInPixels;
}

// Canonical size for `label` (US Letter when missing/unknown), with width/height swapped for landscape.
export function getPageSizeForOrientation(label: string | undefined, orientation: PageOrientation | undefined): PageSize {
  const size = PAGE_SIZES.find((pageSize) => pageSize.label === label) ?? DEFAULT_PAGE_SIZE;
  return orientation === 'landscape' ? { ...size, width: size.height, height: size.width } : size;
}

export function getOrientationFromPageSize(pageSize: PageSize): PageOrientation {
  return pageSize.width > pageSize.height ? 'landscape' : 'portrait';
}

export function flipPageSizeOrientation(pageSize: PageSize): PageSize {
  return { ...pageSize, width: pageSize.height, height: pageSize.width };
}

function isKnownPageSize(pageSize: Partial<PageSize> | null | undefined): pageSize is PageSize {
  return (
    !!pageSize &&
    PAGE_SIZES.some((size) => size.label === pageSize.label) &&
    typeof pageSize.width === 'number' &&
    typeof pageSize.height === 'number'
  );
}

// Normalizes a stored page size into a concrete one: a copy when valid, otherwise the canonical
// size for its label (US Letter when missing/unknown). The backend already reconciles legacy
// pageOrientation into pageSize, so orientation is always derived from the result.
export function resolvePageSize(pageSize?: Partial<PageSize> | null): PageSize {
  if (!isKnownPageSize(pageSize)) return getPageSizeForOrientation(pageSize?.label, undefined);
  return { label: pageSize.label, width: pageSize.width, height: pageSize.height };
}
